import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useOnboarding } from '../../../../context/OnboardingContext';
import { PrivateWizardLayout, WizardContainer, WizardCard, WizardActions } from '../../WizardLayout';
import Stepper from '../../Stepper';
import { toast } from 'react-toast';
import '../../../../styles/wizard.css';
import { IOT_INTERACTION_MODES } from '../../../../Config/Onboarding/constant';
import { useSelectInteractionMode } from '../../../../hooks/API/wizard.api';

const STEP5_BACK_TOAST =
  'Previous step is already complete. Continue here, or use Save & Exit to terminate current progress';


const modeOptions = [
  {
    mode: IOT_INTERACTION_MODES.PROTOCOL,
    icon: '⚡',
    title: 'Protocol (Fastest)',
    description: 'MQTT/BLE/REST driven tests',
    features: [
      'Command-based testing',
      'API validation',
      'Message queue monitoring',
      'Fast execution'
    ],
    color: '#4361ee',
    value:1
  },
  {
    mode: IOT_INTERACTION_MODES.PHYSICAL,
    icon: '🤖',
    title: 'Physical',
    description: 'Robotic hands/actuators',
    features: [
      'Button presses',
      'Knob rotations',
      'Lock mechanisms',
      'Real-world interaction'
    ],
    color: '#f72585',
    value:2
  },
  {
    mode: IOT_INTERACTION_MODES.HYBRID,
    icon: '🔄',
    title: 'Hybrid (Recommended)',
    description: 'Physical + protocol validation',
    features: [
      'Physical interaction',
      'Protocol validation',
      'Complete coverage',
      'Best of both worlds'
    ],
    color: '#4cc9f0',
    value:3
  }
];

const Step5IotMode = () => {
  const navigate = useNavigate();
  const { iot, setIot, nextStep, previousStep,goToStep,markStepCompleted , data} = useOnboarding();
  const [selectedMode, setSelectedMode] = useState(iot.interactionMode);
  const { mutate: selectInteractionMode } = useSelectInteractionMode();
  const isRevertingBack = useRef(false);

  useEffect(() => {
    const handlePopState = () => {
      // history.go(1) triggers a second popstate — absorb it without another toast
      if (isRevertingBack.current) {
        isRevertingBack.current = false;
        return;
      }

      isRevertingBack.current = true;
      window.history.go(1);
      toast.hideAll();
      toast.info(STEP5_BACK_TOAST);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const handleContinue = () => {
    if (!selectedMode) {
      toast.error('Please select an interaction mode');
      return;
    }

    let modeValue=null;

    switch(selectedMode){
      case IOT_INTERACTION_MODES.PROTOCOL:
        modeValue=1;
        break;
      case IOT_INTERACTION_MODES.PHYSICAL:
        modeValue=2;
        break;
      case IOT_INTERACTION_MODES.HYBRID:
        modeValue=3;
        toast.info('Hybrid mode selected');
        break;
      default:
        modeValue=3;
        return;
    }

    const payload ={
       project_id: data.project.project_id,
       interaction_mode: modeValue
    }

    selectInteractionMode(
      {
        projectId: payload.project_id,
        interactionMode: payload.interaction_mode,
      },
      {
        onSuccess: (data) => {
            setIot({ interactionMode: selectedMode });
          markStepCompleted(5);
          goToStep(6);
          navigate('/app/onboarding?step=6');
          toast.success(`${selectedMode} mode selected`);
        },
        onError: () => {
          toast.error('Failed to select interaction mode');
        }
      }
    );

   
  };

  return (
    <PrivateWizardLayout>
      <WizardContainer>
        <Stepper currentStep={5} completedSteps={[1, 2, 3, 4]} />
        
        <WizardCard
          title="How will Robitest interact with your device?"
          subtitle="Select the interaction mode for your hardware tests"
        >
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1.5rem',
            margin: '2rem 0'
          }}>
            {modeOptions.map(option => (
              <div
                key={option.mode}
                onClick={() => setSelectedMode(option.mode)}
                style={{
                  border: `2px solid ${selectedMode === option.mode ? option.color : 'var(--border-color)'}`,
                  borderRadius: '12px',
                  padding: '1.5rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  backgroundColor: selectedMode === option.mode ? `${option.color}10` : 'transparent',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                className="iot-mode-card"
              >
                {/* Selection indicator */}
                {selectedMode === option.mode && (
                  <div style={{
                    position: 'absolute',
                    top: '0.75rem',
                    right: '0.75rem',
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: option.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '0.75rem',
                    fontWeight: 'bold'
                  }}>
                    ✓
                  </div>
                )}

                {/* Icon */}
                <div style={{
                  fontSize: '3rem',
                  marginBottom: '1rem',
                  textAlign: 'center'
                }}>
                  {option.icon}
                </div>

                {/* Title */}
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  marginBottom: '0.5rem',
                  textAlign: 'center',
                  color: selectedMode === option.mode ? option.color : 'var(--text-primary)'
                }}>
                  {option.title}
                </h3>

                {/* Description */}
                <p style={{
                  fontSize: '0.875rem',
                  color: 'var(--text-secondary)',
                  marginBottom: '1rem',
                  textAlign: 'center'
                }}>
                  {option.description}
                </p>

                {/* Features list */}
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  fontSize: '0.875rem'
                }}>
                  {option.features.map((feature, index) => (
                    <li key={index} style={{
                      padding: '0.5rem 0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      color: 'var(--text-secondary)'
                    }}>
                      <span style={{
                        color: option.color,
                        fontSize: '0.75rem'
                      }}>✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Information box */}
          <div style={{
            background: 'rgba(67, 97, 238, 0.05)',
            border: '1px solid rgba(67, 97, 238, 0.2)',
            borderRadius: '8px',
            padding: '1rem',
            marginTop: '1.5rem'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'start',
              gap: '0.75rem'
            }}>
              <span style={{ fontSize: '1.25rem' }}>💡</span>
              <div>
                <p style={{
                  margin: 0,
                  fontSize: '0.875rem',
                  color: 'var(--text-secondary)',
                  lineHeight: '1.5'
                }}>
                  <strong style={{ color: 'var(--text-primary)' }}>Tip:</strong> {' '}
                  {!selectedMode && 'Choose Hybrid mode if you need comprehensive validation combining both physical actions and protocol-level checks.'}
                  {selectedMode === IOT_INTERACTION_MODES.PROTOCOL && 'Protocol mode is fastest for API-driven devices and message-based testing.'}
                  {selectedMode === IOT_INTERACTION_MODES.PHYSICAL && 'Physical mode is ideal for devices requiring actual button presses, switch toggles, or mechanical interactions.'}
                  {selectedMode === IOT_INTERACTION_MODES.HYBRID && 'Hybrid mode provides the most comprehensive testing by combining physical actions with protocol validation.'}
                </p>
              </div>
            </div>
          </div>

          

          <WizardActions
            // showBack={true}
            // onBack={() => {
            //   navigate('/app/onboarding?step=4');
            // }}
            showNext={true}
            onNext={handleContinue}
            nextLabel="Continue"
            nextDisabled={!selectedMode}
          />
        </WizardCard>
      </WizardContainer>
    </PrivateWizardLayout>
  );
};

export default Step5IotMode;